import { ReactElement, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Grid, Typography, Theme, Link } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import { useFormikContext } from 'formik'
import DataTable from '@src/components/DataTable'
import { DataColumn } from '@src/components/DataTable/DataTable'
import {
  SemiConfigFormValues,
  SemiConfigClone,
  useProductFromQueryParam,
  getProductBasedUniqueIdName,
  convertToUppercaseBasedOnCondition,
} from '../../ProductsConfiguration'
import TableExpanded from './TableExpanded'
import SelectAllCheckbox from './SelectAllCheckbox'
import SelectSingleCheckbox from './SelectSingleCheckbox'
import TableRowDetailWithExpansion from './TableRowDetailWithExpansion'
import HoverToolTip from '@src/components/HoverToolTip'
import { SemiConfigProductTypes } from '../../enum'
import DoneRoundedIcon from '@material-ui/icons/DoneRounded'
import PerPageAndSortBy from './PerPageAndSortBy'
import Pagination from './Pagination'
import SequenceViewModel from '@src/components/SequenceViewModel'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    paddingTop: theme.spacing(8),
  },
  instructions: {
    paddingTop: theme.spacing(6),
    '& h6': {
      fontWeight: 700,
    },
  },
  table: {
    display: 'flex',
    '& th': {
      verticalAlign: 'middle',
    },
    '& .MuiTableCell-root': {
      padding: theme.spacing(2, 1),
    },
  },
  tooltipClass: {
    maxWidth: 'none',
    width: 'auto',
  },
  topChoice: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  topChoiceIcon: {
    color: theme.palette.primary.main,
  },
  paddingBottom: {
    paddingBottom: theme.spacing(4),
  },
  sequenceView: {
    color: 'rgb(15, 105, 175)',
    fontWeight: theme.typography.fontWeightMedium,
  },
}))

interface ClonesTableProps {
  tableTitle: ReactElement
  tableData: SemiConfigClone[]
  totalCount: number
  showPaginationAndSort?: boolean
}

const ClonesTable = ({
  tableTitle,
  tableData,
  totalCount,
  showPaginationAndSort = false,
}: ClonesTableProps) => {
  const classes = useStyles()
  const product = useProductFromQueryParam()
  const formik = useFormikContext<SemiConfigFormValues>()
  const uniqueIdName = getProductBasedUniqueIdName(product)
  const [expandedList, setExpandedList] = useState<string[]>([])
  const [item, setItem] = useState<any>()
  const [dialogOpen, setDialogOpen] = useState(false)
  const {
    SANGERCRISPR,
    ORF,
    PREDESIGNED_SIRNA,
    PREDESIGNED_CRISPR_GRNA,
    PREDESIGNED_SHRNA,
  } = SemiConfigProductTypes

  let pageStart = totalCount > 0 ? 1 : 0
  let pageEnd = totalCount || 0
  if (
    showPaginationAndSort &&
    totalCount > 0 &&
    totalCount !== parseInt(formik.values.searchData.count)
  ) {
    pageStart =
      (formik.values.searchData.page - 1) *
        parseInt(formik.values.searchData.count) +
      1
    pageEnd =
      formik.values.searchData.page * parseInt(formik.values.searchData.count) >
      totalCount
        ? totalCount
        : formik.values.searchData.page *
          parseInt(formik.values.searchData.count)
  }

  const toggleDetail = (id: string) => {
    const updatedList = expandedList.includes(id)
      ? expandedList.filter((expanded) => expanded !== id)
      : [...expandedList, id]
    setExpandedList(updatedList)
  }
  const checkForExpanded = (id?: string) => {
    return !!(id && expandedList.includes(id))
  }
  const getRowId = (data: SemiConfigClone) => {
    switch (product) {
      case PREDESIGNED_CRISPR_GRNA:
        return data?.grnaId || ''
      case SANGERCRISPR:
        return data?.sangerCloneId || ''
      case ORF:
        return data?.cloneId || ''
      case PREDESIGNED_SHRNA:
        return data?.cloneId || ''
      default:
        return data?.[uniqueIdName] || ''
    }
  }

  const viewORFSequence = (item) => {
    setDialogOpen(true)
    setItem(item)
  }

  const getProductBasedTableColumns = (): DataColumn<SemiConfigClone>[] => {
    const checkBoxColumn: DataColumn = {
      headElement: <SelectAllCheckbox searchResults={tableData} />,
      bodyElement: (data: SemiConfigClone) => (
        <SelectSingleCheckbox clone={data} />
      ),
      width: '5%',
      textAlign: 'center',
    }
    const geneIdColumn: DataColumn = {
      headElement: (
        <FormattedMessage id="CONF_CRISPR_GENE_ID" defaultMessage="Gene ID" />
      ),
      bodyElement: (data: SemiConfigClone) =>
        convertToUppercaseBasedOnCondition(data?.geneId || data?.ncbiGeneId),
      width: '10%',
      textAlign: 'center',
    }
    const speciesColumn: DataColumn = {
      headElement: (
        <FormattedMessage
          id="CONF_SPECIES_crisprgrna"
          defaultMessage="Species"
        />
      ),
      bodyElement: (data) => convertToUppercaseBasedOnCondition(data?.species),
      width: '10%',
      textAlign: 'center',
    }
    const detailsColumn: DataColumn = {
      headElement: <></>,
      bodyElement: (data) => {
        const rowId = getRowId(data)
        return (
          <TableRowDetailWithExpansion
            id={rowId}
            isExpanded={checkForExpanded(rowId)}
            onClick={toggleDetail}
          />
        )
      },
      width: '10%',
      textAlign: 'center',
    }

    switch (product) {
      case PREDESIGNED_CRISPR_GRNA:
        return [
          checkBoxColumn,
          {
            headElement: (
              <FormattedMessage
                id="PREDESIGNED_CONTROL_MISSION_GRNA_ID"
                defaultMessage="MISSION™ gRNA ID"
              />
            ),
            bodyElement: (data) => data?.grnaId,
            textAlign: 'center',
            width: '18%',
          },
          {
            headElement: (
              <FormattedMessage
                id="CONF_CATEGORY_crisprgrna"
                defaultMessage="gRNA Category"
              />
            ),
            bodyElement: (data) => {
              return data?.recommendedStatus === 'Recommended' ? (
                <div className={classes.topChoice}>
                  <DoneRoundedIcon className={classes.topChoiceIcon} />
                  <FormattedMessage
                    id="SEMI_CONF_TOP_CHOICE_crispgrna"
                    defaultMessage="Top Choice"
                  />
                </div>
              ) : (
                data?.recommendedStatus
              )
            },
            width: '14%',
            textAlign: 'center',
          },
          {
            headElement: (
              <FormattedMessage
                id="CONF_CRISPR_GENE_SYMBOL"
                defaultMessage="Gene Symbol"
              />
            ),
            bodyElement: (data) =>
              convertToUppercaseBasedOnCondition(data?.geneSymbol),
            width: '10%',
            textAlign: 'center',
          },
          geneIdColumn,
          {
            headElement: (
              <FormattedMessage
                id="CONF_EFFICIENCY_LABEL_crispgrna"
                defaultMessage="Efficiency Score"
              />
            ),
            headElementEndIcon: (
              <HoverToolTip
                toolTipText={
                  <div>
                    <FormattedMessage
                      id="SEMICONF_HELP_ICON_TEXT_EFFICIENCY_crisprgrna"
                      defaultMessage="Efficiency score from Doench, Fusi et al. (2016)"
                    />
                  </div>
                }
                fontSize="14"
                tooltipClass={classes.tooltipClass || undefined}
              />
            ),
            bodyElement: (data) =>
              convertToUppercaseBasedOnCondition(data?.efficiencyScore),
            width: '14%',
            textAlign: 'center',
          },
          {
            headElement: (
              <FormattedMessage
                id="CONF_SPECIFICATION_LABEL_crispgrna"
                defaultMessage="Specificity Score"
              />
            ),
            headElementEndIcon: (
              <HoverToolTip
                toolTipText={
                  <div>
                    <FormattedMessage
                      id="SEMICONF_HELP_ICON_TEXT_SPECIFICITY_CRISPRGRNA_UPDATED"
                      defaultMessage="Specificity score from Hsu et al. (2013)"
                    />
                  </div>
                }
                fontSize="14"
              />
            ),
            bodyElement: (data) =>
              convertToUppercaseBasedOnCondition(data?.specificityScore),
            width: '14%',
            textAlign: 'center',
          },
          speciesColumn,
          detailsColumn,
        ]

      case SANGERCRISPR:
        return [
          checkBoxColumn,
          {
            headElement: (
              <FormattedMessage
                id="SANGER_CLONE_ID"
                defaultMessage="Sanger{lineBreak}Clone ID"
                values={{ lineBreak: <br /> }}
              />
            ),
            bodyElement: (data) => data?.sangerCloneId,
            textAlign: 'left',
            width: '15%',
          },
          {
            headElement: (
              <FormattedMessage
                id="ENSEMBL_GENE_SYMBOL"
                defaultMessage="Ensembl{lineBreak}Gene Symbol"
                values={{ lineBreak: <br /> }}
              />
            ),
            bodyElement: (data) =>
              convertToUppercaseBasedOnCondition(data?.ensemblGeneSymbol),
            width: '10%',
            textAlign: 'left',
          },
          {
            headElement: (
              <FormattedMessage
                id="DNA_TARGET_SEQUENCE"
                defaultMessage="DNA{lineBreak}Target Sequence"
                values={{ lineBreak: <br /> }}
              />
            ),
            bodyElement: (data) =>
              convertToUppercaseBasedOnCondition(data?.targetSeq),
            width: '27%',
            textAlign: 'left',
          },
          {
            headElement: (
              <FormattedMessage
                id="NCBI_GENE_SYMBOL"
                defaultMessage="NCBI{lineBreak}Gene Symbol"
                values={{ lineBreak: <br /> }}
              />
            ),
            bodyElement: (data) =>
              convertToUppercaseBasedOnCondition(data?.ncbiGeneSymbol),
            width: '12%',
            textAlign: 'left',
          },
          {
            headElement: (
              <FormattedMessage
                id="NCBI_GENE_ID"
                defaultMessage="NCBI{lineBreak}Gene ID"
                values={{ lineBreak: <br /> }}
              />
            ),
            bodyElement: (data) =>
              convertToUppercaseBasedOnCondition(data?.ncbiGeneId),
            width: '10%',
            textAlign: 'left',
          },
          speciesColumn,
          detailsColumn,
        ]

      case ORF:
        return [
          checkBoxColumn,
          {
            headElement: (
              <FormattedMessage
                id="TRC_CLONE_ID"
                defaultMessage="TRC Clone ID"
                values={{ lineBreak: <br /> }}
              />
            ),
            bodyElement: (data) => data?.cloneId,
            textAlign: 'left',
            width: '15%',
          },
          {
            headElement: (
              <FormattedMessage
                id="ORF_GENE_SYMBOL"
                defaultMessage="Gene Symbol"
              />
            ),
            bodyElement: (data) => {
              return (
                <div style={{ textAlign: 'left' }}>
                  {data?.subSetInfo?.map((subset, index) =>
                    subset.refSeqId.map(() => (
                      <div key={index}>
                        {convertToUppercaseBasedOnCondition(subset?.geneSymbol)}
                      </div>
                    ))
                  )}
                </div>
              )
            },
            textAlign: 'left',
            width: '10%',
          },
          {
            headElement: (
              <FormattedMessage id="ORF_GENE_ID" defaultMessage="Gene ID" />
            ),
            bodyElement: (data) => {
              return (
                <div style={{ textAlign: 'left' }}>
                  {data?.subSetInfo?.map((subset, index) =>
                    subset.refSeqId.map(() => (
                      <div key={index}>
                        {convertToUppercaseBasedOnCondition(subset?.geneId)}
                      </div>
                    ))
                  )}
                </div>
              )
            },
            textAlign: 'left',
            width: '10%',
          },
          {
            headElement: (
              <FormattedMessage
                id="ORF_REF_SEQ_ID"
                defaultMessage="Ref Seq ID"
              />
            ),
            bodyElement: (data) => {
              return (
                <div style={{ textAlign: 'left' }}>
                  {data?.subSetInfo?.map((subset, index) => (
                    <div key={index}>
                      {subset?.refSeqId?.map((id, i) => (
                        <div key={i}>
                          {convertToUppercaseBasedOnCondition(id)}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )
            },
            textAlign: 'left',
            width: '10%',
          },
          {
            headElement: (
              <FormattedMessage
                id="ORF_MATCH_TYPE"
                defaultMessage="Match Type"
              />
            ),
            bodyElement: (data) => {
              return (
                <div style={{ textAlign: 'left' }}>
                  {data?.subSetInfo?.map((subset, index) =>
                    subset.refSeqId.map(() => (
                      <div key={index}>
                        {convertToUppercaseBasedOnCondition(subset?.matchType)}
                      </div>
                    ))
                  )}
                </div>
              )
            },
            textAlign: 'left',
            width: '10%',
          },
          {
            headElement: (
              <FormattedMessage
                id="ORF_LENGTH"
                defaultMessage="ORF Length"
                values={{ lineBreak: <br /> }}
              />
            ),
            bodyElement: (data) =>
              convertToUppercaseBasedOnCondition(data?.orfLength),
            width: '10%',
            textAlign: 'left',
          },
          {
            headElement: (
              <FormattedMessage
                id="ORF_SEQUENCE"
                defaultMessage="ORF Sequence"
                values={{ lineBreak: <br /> }}
              />
            ),
            bodyElement: (data) => (
              <Link
                href="#"
                underline="hover"
                className={classes.sequenceView}
                onClick={() => {
                  viewORFSequence(data)
                }}
              >
                <FormattedMessage
                  id="ORF_ORF_SEQUENCE_VIEW"
                  defaultMessage="View"
                />
              </Link>
            ),
            width: '10%',
            textAlign: 'left',
          },
        ]

      case PREDESIGNED_SIRNA:
      case PREDESIGNED_SHRNA:
        return [
          checkBoxColumn,
          {
            headElement: (
              <FormattedMessage
                id="TRC_CLONE_ID"
                defaultMessage="TRC Clone ID"
                values={{ lineBreak: <br /> }}
              />
            ),
            bodyElement: (data) => data?.cloneId,
            textAlign: 'left',
            width: '15%',
          },
          {
            headElement: (
              <FormattedMessage
                id="CONF_VALIDATION"
                defaultMessage="Validation"
              />
            ),
            bodyElement: (data) => {
              return data?.validated === 'Yes' ? (
                <div className={classes.topChoice}>
                  <FormattedMessage
                    id="SEMI_CONF_VALIDATED"
                    defaultMessage="Validated"
                  />
                </div>
              ) : (
                ''
              )
            },
            width: '14%',
            textAlign: 'center',
          },
          {
            headElement: (
              <FormattedMessage
                id="CONF_CRISPR_GENE_SYMBOL"
                defaultMessage="Gene Symbol"
              />
            ),
            bodyElement: (data) =>
              convertToUppercaseBasedOnCondition(data?.geneSymbol),
            width: '10%',
            textAlign: 'center',
          },
          geneIdColumn,
          {
            headElement: (
              <FormattedMessage id="SHRNA_REF_SEQ" defaultMessage="Ref Seq" />
            ),
            bodyElement: (data) => {
              return (
                <div style={{ textAlign: 'center' }}>
                  {data?.subSetInfo?.map((subset, index) => (
                    <div key={index}>
                      {subset?.refSeqId?.map((id, i) => (
                        <div key={i}>
                          {convertToUppercaseBasedOnCondition(id)}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )
            },
            width: '10%',
            textAlign: 'center',
          },
          speciesColumn,
          {
            headElement: (
              <FormattedMessage id="CONF_REGION" defaultMessage="Region" />
            ),
            bodyElement: (data) =>
              convertToUppercaseBasedOnCondition(data?.region),
            width: '14%',
            textAlign: 'center',
          },

          detailsColumn,
        ]
      default:
        return []
    }
  }

  const noResultsComponent = (
    <FormattedMessage id="NO_RESULTS_FOUND" defaultMessage="No results found" />
  )

  const tableExpandedComponent = (data: SemiConfigClone) => {
    const rowId = getRowId(data)
    return expandedList.includes(rowId) ? (
      <TableExpanded
        data={
          product === SANGERCRISPR
            ? {
                ensemblGeneId: data.ensemblGeneId,
                exonId: data.exonId,
              }
            : product === PREDESIGNED_SHRNA
              ? {
                  targetSeq: data.targetSeq,
                  targetSequence: data.targetSeq,
                  synonyms: (data as any).synonyms || '',
                  cellType: (data as any).cellType || '',
                  method: (data as any).method || '',
                  knockdown: (data as any).knockdown || '',
                  trcVersion: (data as any).trcVersion || '',
                }
              : data.moreInformation
        }
        product={product}
      />
    ) : (
      <></>
    )
  }

  return (
    <Grid container className={classes.container}>
      <Grid item xs={8} className={classes.paddingBottom}>
        <Grid item xs={12}>
          <Typography variant="h3">{tableTitle}</Typography>
        </Grid>
        <Grid item xs={12} className={classes.instructions}>
          <Typography variant="subtitle1">
            <FormattedMessage
              id="WEBINAR_PAGINATION"
              defaultMessage="Showing {pageStart} - {pageEnd} of {numberOfPages} Results"
              values={{
                pageStart: pageStart,
                pageEnd: pageEnd,
                numberOfPages: totalCount,
              }}
            />
          </Typography>
        </Grid>
      </Grid>
      {showPaginationAndSort && <PerPageAndSortBy totalCount={totalCount} />}

      <Grid item xs={12} className={classes.table}>
        <DataTable
          data={tableData}
          columns={getProductBasedTableColumns()}
          noDataComponent={noResultsComponent}
          captionText={tableTitle}
          afterRow={tableExpandedComponent}
        />
      </Grid>
      {showPaginationAndSort && <Pagination totalCount={totalCount} />}
      <SequenceViewModel
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        item={item}
      />
    </Grid>
  )
}

export default ClonesTable
